import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { GenerateUrlRequestBody, Paginated, PaginationQuery, UpdateOrderRequestBody } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  FullModule,
  ModuleSettings,
  SearchModule,
  UpsertModuleNewImageBody,
  UpsertModuleRemoveImageBody,
  UpsertModuleSettings,
} from "./types/modules";

export const useModulesApi = () => {
  const api = useApi();

  // Admin endpoints

  // Get a single module
  const getAdminModule = (id: string, signal: AbortSignal) => {
    return useSWRMutation<FullModule, CustomError, string>(`/admin/modules/${id}`, (url) =>
      api.get<FullModule>({ url, signal }),
    );
  };

  // Get a paginated list of modules
  const useAdminModules = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl("/admin/modules", query);
    return useSWR<Paginated<SearchModule>, CustomError, string>(url, (url) =>
      api.get<Paginated<SearchModule>>({ url, signal }),
    );
  };

  // A page of modules, e.g. the options of an AsyncSelect (see useAsyncOptions)
  const searchAdminModules = useCallback(
    (query: PaginationQuery, signal: AbortSignal): Promise<SearchModule[]> =>
      api.get<Paginated<SearchModule>>({ url: buildUrl("/admin/modules", query), signal }).then((x) => x.items),
    [api],
  );

  // Update an existing module
  const updateAdminModule = useCallback(
    async (moduleId: string, body: UpsertModuleRemoveImageBody | UpsertModuleNewImageBody): Promise<void> => {
      const url = `/admin/modules/${moduleId}`;
      await api.put<UpsertModuleRemoveImageBody | UpsertModuleNewImageBody>(url, body);
      mutateCache(["/admin/modules", `/admin/modules/${moduleId}`, "/modules"], url);
    },
    [],
  );

  const updateAdminModuleSettings = useCallback(async (moduleId: string, body: UpsertModuleSettings): Promise<void> => {
    const url = `/admin/modules/${moduleId}/settings`;
    await api.put<UpsertModuleSettings>(url, body);
    mutateCache(["/admin/modules", `/admin/modules/${moduleId}`, `/admin/modules/${moduleId}/settings`, "/modules"]);
  }, []);

  // generate image url - todo move to user endpoint?
  const generateUploadImageUrl = useCallback(
    async (moduleId: string, fileName: string, fileType: string): Promise<string> => {
      const url = `/admin/modules/${moduleId}/image/generate_upload_url`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [],
  );

  // get module settings
  const getAdminModuleSettings = (moduleId: string, signal: AbortSignal) => {
    return useSWRMutation<ModuleSettings, CustomError, string>(`/admin/modules/${moduleId}/settings`, (url) =>
      api.get<ModuleSettings>({ url, signal }),
    );
  };

  const updateAdminModuleOrder = useCallback(
    async (moduleId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/modules/${moduleId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/modules", "/modules"]);
    },
    [],
  );

  // public endopoints

  // Get a paginated list of modules
  const useModules = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl("/modules", query);
    return useSWR<Paginated<SearchModule>, CustomError, string>(url, (url) =>
      api.get<Paginated<SearchModule>>({ url, signal }),
    );
  };

  // Get singles module for user. Pass id=undefined to skip fetching (e.g. not in a single-module context).
  const getModule = (id: string | undefined, signal: AbortSignal) => {
    const url = id ? `/modules/${id}` : null;
    return useSWR<FullModule, CustomError, string | null>(url, () => api.get<FullModule>({ url: url!, signal }));
  };

  return {
    getAdminModule,
    useAdminModules,
    searchAdminModules,
    updateAdminModule,
    generateUploadImageUrl,
    updateAdminModuleOrder,

    useModules,
    getModule,

    getAdminModuleSettings,
    updateAdminModuleSettings,
  };
};
